<template>
  <loading-overlay v-if="haveData" :loading="loading" :show-spinner="loading">
    <va-card>
      <div class="row row-equal">
        <div class="flex xs11 text-right"><va-button v-if="isZoomed" @click="reload()">Unzoom</va-button><br /></div>
        <div class="flex xs1">
          <va-button @click="showChartHelp = !showChartHelp">?</va-button>
        </div>
      </div>
      <div class="row row-equal">
        <div class="flex xs12 xl12" ref="plotdiv">
          <Plotly :data="pdata" :layout="playout" :display-mode-bar="false" @relayout="plotlyRelayout"></Plotly>
        </div>
      </div>
      <div class="row row-equal">
        <div class="flex xs12 md3">
          <va-select label="Left axis" v-model="selFieldsLeftModel" :options="selFieldsLeftOptions" :noClear="true" maxHeight="10" />
          <va-select v-if="!isSiteSummary" label="Right axis" v-model="selFieldsRightModel" :options="selFieldsRightOptions" :noClear="true" maxHeight="10" :disabled="isRightDisabled" />
        </div>

        <div class="flex xs12 md3">
          <va-select v-if="isSiteSummary" label="Where" v-model="selInvsModel" :options="selInvsOptionsSite" :tagMax="4" :multiple="true" :searchable="false" maxHeight="10" :clearValue="defaultInvsModel" :noClear="true" />
          <va-select v-if="!isSiteSummary" label="Where" v-model="selInvsModel" :options="selInvsOptions" :tagMax="4" :multiple="true" :searchable="true" maxHeight="10" :clearValue="defaultInvsModel" />
          <va-select v-if="showRes" label="resolution" v-model="resModel" :options="resOptions" maxHeight="10" :noClear="true" :clearValue="resOptions[0]" />
        </div>
        <div class="flex xs12 md3">
          <va-select label="when" v-model="selDatesModel" :options="selDatesOptions" maxHeight="10" :noClear="true" :clearValue="selDatesOptions[0]" @input="selDatesSelect" />
        </div>
        <div class="flex xs12 md3">
          <va-button @click="reload()">Reload</va-button>
          <va-button @click="download()">Download CSV</va-button>

          <va-modal v-model="showChartHelp" title :hideDefaultActions="true">
            <table>
              <tr>
                <td class="chart-help-item">Click-drag</td>
                <td class="chart-help-text">
                  Zoom in.
                  <br />Drag horizontally or vertically to zoom only that direction. <br />Drag diagonally to zoom both directions.
                </td>
              </tr>
              <tr>
                <td class="chart-help-item">Double-click</td>
                <td class="chart-help-text">Unzoom</td>
              </tr>
              <tr>
                <td class="chart-help-item">Shift-drag</td>
                <td class="chart-help-text">Pan (when zoomed)</td>
              </tr>
              <tr>
                <td class="chart-help-item">Click item in legend</td>
                <td class="chart-help-text">Hide / show that item</td>
              </tr>
              <tr>
                <td class="chart-help-item">Double-click item in legend</td>
                <td class="chart-help-text">Hide all but that item</td>
              </tr>
            </table>
          </va-modal>
        </div>
      </div>
      <va-modal v-model="showingDatePicker" hideDefaultActions>
        <va-date-picker
          label="Date range"
          v-model="selDatesRange"
          :config="{ mode: 'range', inline: true, maxDate: new Date() }"
          @input="datePickerPicked"
        />
      </va-modal>
    </va-card>
  </loading-overlay>
</template>

<script>
import { getDonutChartData } from '../../data/charts/DonutChartData'
import { getLineChartData } from '../../data/charts/LineChartData'
import LoadingOverlay from './LoadingOverlay'
import { Plotly } from 'vue-plotly'
import hiqapi from '@/hiqapi/hiqapi'
import { dateFromDuration, diffMins } from '@/common'

export default {
  name: 'dashboard-charts',
  props: ['location'],
  components: { Plotly, LoadingOverlay },
  data() {
    return {
      showChartHelp: false,
      isZoomed: false,

      inverterList: [],

      selFieldsLeftOptions: this.getSelFieldsOptions(true),
      selFieldsRightOptions: this.getSelFieldsOptions(false),

      selFieldsLeftModel: this.getSelFieldsOptions(true)[0],
      selFieldsRightModel: this.getSelFieldsOptions(false)[0],

      selInvsModel: [],
      // When we are first loaded, the default "WHERE" (i.e. selInvsModel) is "Gateway", but this takes a long time to load
      // and we don't really want it. Since we don't know which item in selInvsModel to choose at this time, (but there has
      // to be a default), the following flag will be true to start with until we have fully loaded and actually chosen
      // the correct "default" inv (the first one in the list). This prevents going to the server (and dealing with the response)
      // before we know what it is we want to get.
      readyToLoadData: false,

      selDatesOptions: [
        { id: '3day', text: 'Past 3 days' },
        { id: '7day', text: 'Past week' },
        { id: '1mon', text: 'Past month' },
        { id: '3mon', text: 'Past 3 months' },
        { id: 'range', text: 'Date Range' },
      ],
      selDatesModel: '',

      selDatesRange: '',
      showingDatePicker: false,

      resModel: '',
      resOptions: [
        { id: '15m', text: '15 minutes' },
        { id: '1h', text: '1 hour' },
        { id: '1d', text: '1 day' },
        { id: '1mon', text: '1 month' },
      ],

      // showTypeModal: false,
      lineChartData: getLineChartData(this.$themes),
      donutChartData: getDonutChartData(this.$themes),
      progressMax: 328,

      pdata: [],
      playout: {
        margin: { t: 0 }, // Top margin
        xaxis: {},
        // yaxis: { // cloud cover
        //   showgrid: false,
        //   showticklabels: false,
        //   range: [0, 100],
        //   // hoverlabel: { font: { color: 'black'}}, doesn't work

        // },
        yaxis2: {
          // left side
          overlaying: 'y3',
          fixedrange: true, // not zoomable

          rangemode: 'tozero',
        },
        yaxis3: {
          // right side
          // overlaying: 'y2',
          fixedrange: true,
          rangemode: 'tozero',
          side: 'right',
          showgrid: false,
          zeroline: false,
        },
        legend: {
          // xanchor: 'center',
          yanchor: 'top',
          y: -0.2,
          x: 0,
          orientation: 'h',
        },
      },
      chartWidth: 0, // current width of plotted data area

      // received data saved for csv generation. One entry in here is one response from the server, so if
      // there only left axis is used, there will be only on entry. If right axis is used, there will be two.

      dataForCsv: [],

      tmp: 0,
      loadingCount: 0, // the number if requests pending (see computed loading())
      prettyFieldNames: null,
    }
  },
  watch: {
    '$themes.primary'() {
      this.lineChartData = getLineChartData(this.$themes)
      this.donutChartData = getDonutChartData(this.$themes)
    },

    '$themes.info'() {
      this.lineChartData = getLineChartData(this.$themes)
      this.donutChartData = getDonutChartData(this.$themes)
    },

    '$themes.danger'() {
      this.donutChartData = getDonutChartData(this.$themes)
    },
    location: {
      immediate: true, // so this is called at startup (in addition to subsequent changes)
      deep: true,
      handler(val, oldVal) {
        if (!val.selectedId) {
          console.log('DC-LOCATION ignoring location change because selectedId not set')
          return
        }
        this.readyToLoadData = true
        console.log(`DC-LOCATION will get chart for site=${val.siteId} gw=${val.gwSerial} type=${val.listType} sel=${val.selectedId}`)
        this.selInvsModel = [{ id: val.selectedId, text: `#${val.selectedId}` }]

        if (val.listType !== 'xsites') {
          this.loadingCount = 1
          // TODO: I don't need _all_ this data, just the list of inverters
          hiqapi.getcb(`/inverters?last_gw_serial=${val.gwSerial ? val.gwSerial : 0}&site_id=${val.siteId}&summary=true`, this.setInverterList)
        }
      },
    },
    // selDatesModel () {
    //   console.log('DC RELOAD new date is ', this.selDatesModel)
    //   this.needReload = true
    // },
  },
  computed: {
    loading() {
      return this.loadingCount > 0
    },
    haveData() {
      return !!this.inverterList
    },
    selInvsOptions() {
      let res = []
      res.push({ id: 0, text: 'Gateway', solitary: true })
      res.push({ id: 1, text: 'All Inverters', solitary: true })

      this.inverterList.forEach(function(item) {
        res.push({ id: item.serial, text: '#' + item.serial })
      })
      return res
    },
    selInvsOptionsSite() {
      let res = []
      res.push({ id: 0, text: 'All Gateways', solitary: true })
      return res
    },

    defaultInvsModel() {
      if (this.location.listType === this.location.TYPE_SITES) {
        return [this.selInvsOptionsSite[0]]
      }
      if (this.location.listType === this.location.TYPE_GWS) {
        return [this.selInvsOptions[0]]
      }
      if (this.location.listType === this.location.TYPE_INVS) {
        // return [this.selInvsOptions[0]]

        console.log(`defaultInvsMode() looking for ${this.location.selectedId}`)
        console.log(`defaultInvsMode() found ${[this.selInvsOptions.find((item) => item.id === this.location.selectedId)]}`)
        return [this.selInvsOptions.find((item) => item.id === this.location.selectedId)]
      }
    },

    selectedInvIds() {
      console.log(`zzz  selInvsModel = ${this.selInvsModel}`, this.selInvsModel)
      console.log(`zzz selectedInvIds ==>  ${this.selInvsModel} && ${this.selInvsModel.length} > 0`)
      if (this.selInvsModel && this.selInvsModel.length > 0) {
        return this.selInvsModel.map((a) => a.id)
      }
      return []
    },
    isRightDisabled() {
      return this.selFieldsLeftModel.id === 'invcomp'
    },
    showRes() {
      return this.selFieldsLeftModel.id === 'kwh'
    },
    isSiteSummary() {
      return this.location.listType === 'sites'
    },
    isGwSummary() {
      return this.selectedInvIds[0] === 0 && !this.isSiteSummary
    },
    gwSerial() {
      if (this.isSiteSummary) {
        return 0
      }
      return this.location.gwSerial
    },
  },

  methods: {
    selDatesSelect(val) {
      if (val.id !== 'range') {
        return
      }
      this.showingDatePicker = true
      // console.log("SEL DATE:", val.id)
      // console.log(val)
    },
    datePickerPicked(val) {
      if (val.length < 12) {
        return // just first date was picked
      }
      this.showingDatePicker = false
      this.selDatesOptions[4].text = this.selDatesRange
      this.selDatesModel = this.selDatesOptions[4]
    },
    plotlyRelayout(info) {
      this.isZoomed = true
      console.log('DC Plotly relayout: ', info)
      // info is a weird object...
      if (this.chartWidth === 0 || ('xaxis.autorange' in info && info['xaxis.autorange'])) {
        // This is the width of the chart before we give it data. After we give it data, then width might change slightly because
        // the legend may change size. But it is close enough.
        this.chartWidth = this.$refs.plotdiv.querySelector('.cartesianlayer').getClientRects()[0].width
        console.log(`    width=${this.chartWidth}`)

        this.reload() // reload with user's requested dates
        return
      }

      if ('xaxis.range[0]' in info) {
        let startDate = info['xaxis.range[0]']
        let endDate = info['xaxis.range[1]']
        // dates are "YYYY-MM-DD hh:mm:ss" but they need to be "YYYY-MM-DDThh:mm:ss"
        startDate = startDate.substr(0, 10) + 'T' + startDate.substr(11)
        endDate = endDate.substr(0, 10) + 'T' + endDate.substr(11)
        this.reloadChart(startDate, endDate)
      }
    },
    getFieldLabel(id) {
      const labels = {
        power: 'Watts',
        kwh: 'kWh',
        watts: 'Watts',
        volts: 'Volts',
        temps: 'C',
        moisture: '%',
        plc: 'Value',
        vrms: 'Volts',
        vpeak: 'Volts',
        irms: 'Amps',
        ipeak: 'Amps',
        freq: 'Hz',
        antiisland: 'Value',
        invcomp: 'kWh',
      }
      if (id in labels) {
        return labels[id]
      }
      console.log('DC ERROR unknown field label: ', id)
      return id
    },
    getSelFieldsOptions(isLeft) {
      let res = [
        { id: 'power', text: 'Power' },
        { id: 'watts', text: 'DC Watts' },
        { id: 'volts', text: 'DC Volts' },
        { id: 'temps', text: 'Temps' },
        { id: 'moisture', text: 'Moisture' },
        { id: 'plc', text: 'PLC' },
        { id: 'vrms', text: 'Vrms' },
        { id: 'vpeak', text: 'Vpeak' },
        { id: 'irms', text: 'Irms' },
        { id: 'ipeak', text: 'Ipeak' },
        { id: 'freq', text: 'Frequency' },
        { id: 'antiisland', text: 'Anti-island' },
      ]
      if (isLeft) {
        res.splice(1, 0, { id: 'kwh', text: 'kWh' })
        res.push({ id: 'invcomp', text: 'Inverter Comparison' })
        return res
      }
      return [{ id: 'none', text: '(None)' }, ...res]
    },
    // selInvsVisible(value) {
    // //   if (!value) {
    // //     console.log('DC RELOAD ' + this.selInvsModel.join(','))
    // //     this.needReload = true
    // //   }
    // },
    // selFieldsVisible(value) {
    // //   if (!value) {
    // //     console.log('DC RELOAD ' + this.selFieldsModel.join(','))
    // //     this.needReload = true
    // //   }
    // },
    mapField(field, getFields) {
      if (field === 'power') {
        return ['main', ['output_watts']]
      }
      if (field === 'kwh') {
        return ['kwh', ['kwh']]
      }
      if (field === 'invcomp') {
        return ['invcomp', ['kwh']]
      }
      if (field === 'watts') {
        return ['main', ['string1_w', 'string2_w']]
      }
      if (field === 'volts') {
        return ['main', ['string1_v', 'string2_v']]
      }
      if (field === 'temps') {
        return ['health', ['temp1', 'temp2', 'temp3']]
      }
      if (field === 'moisture') {
        return ['health', ['condensation']]
      }
      if (field === 'plc') {
        return ['main', ['plc_signal', 'plc_noise']]
      }
      if (field === 'vrms') {
        return ['ac', ['phase1_vrms_min', 'phase1_vrms_max', 'phase2_vrms_min', 'phase2_vrms_max', 'phase3_vrms_min', 'phase3_vrms_max']]
      }
      if (field === 'vpeak') {
        return ['ac', ['phase1_vpeak_min', 'phase1_vpeak_max', 'phase2_vpeak_min', 'phase2_vpeak_max', 'phase3_vpeak_min', 'phase3_vpeak_max']]
      }
      if (field === 'irms') {
        return ['ac', ['phase1_irms_min', 'phase1_irms_max', 'phase2_irms_min', 'phase2_irms_max', 'phase3_irms_min', 'phase3_irms_max']]
      }
      if (field === 'ipeak') {
        return ['ac', ['phase1_ipeak_min', 'phase1_ipeak_max', 'phase2_ipeak_min', 'phase2_ipeak_max', 'phase3_ipeak_min', 'phase3_ipeak_max']]
      }
      if (field === 'freq') {
        return ['ac', ['freq_min', 'freq_max']]
      }
      if (field === 'antiisland') {
        return ['ac', ['anti_island_min', 'anti_island_max']]
      }
    },

    reload() {
      console.log('DC about to reload')
      this.isZoomed = false

      let dates = dateFromDuration(this.selDatesModel)

      return this.reloadChart(dates[0], dates[1])
    },

    download() {
      console.log('DC about to download')
      console.log(this.dataForCsv)

      if (this.dataForCsv.length === 0 || this.dataForCsv[0].length === 0) {
        return
      }
      if ('date' in this.dataForCsv[0][0]) {
        this.downloadKwh() // different format
        return
      }
      // dataForCsv looks like this:
      //          x is num fields * num serials.  So for temp1,temp2,temp3 for 5 inverters is: 3*5=15 (i.e. x=0..14)
      //                                          And for output_watts for 2 inverters is: 1*2=2
      //          y is num rows and is the same for all entries
      //  [x].dates[y]
      //  [x].field = "temp1"
      //  [x].inv_serial = '1234'
      //  [x].values_max[y]
      //  [x].values_min[y]
      //
      // So essentially each x is dates and 2 columns (min and max) for inv_serial + field. All dates for all [x] should be identical

      var data = [] // data[<date>][<inv>][<field>][min]   and [max]
      // Note that not all fields have the same dates!
      var fieldNames = new Set()
      var invSerials = new Set()

      this.dataForCsv.forEach((items) => {
        items.forEach((item) => {
          // if (!this.selFieldsLeftGetFields[1].includes(item.field)) {
          //   return
          // }
          var fn = this.prettyFieldName(item.field)
          console.log('PRETTY ', item.field, fn)
          var fnMin = `${fn} (min)`
          var fnMax = `${fn} (max)`

          fieldNames.add(fnMin)
          fieldNames.add(fnMax)

          invSerials.add(item.inv_serial)

          for (let i = 0; i < item.dates.length; i++) {
            let valDate = item.dates[i]

            if (!(valDate in data)) {
              data[valDate] = []
            }

            if (!(item.inv_serial in data[valDate])) {
              data[valDate][item.inv_serial] = []
            }

            data[valDate][item.inv_serial][fnMin] = item.values_min[i]
            data[valDate][item.inv_serial][fnMax] = item.values_max[i]
          }
        })
      })

      // console.log('data=', data)
      // console.log(fieldNames)

      var sFieldNames = Array.from(fieldNames).sort()
      var sInvSerials = Array.from(invSerials).sort()

      var headingItems = ['Date (UTC)']
      var headingRow = null

      var csvRows = []

      Object.keys(data).forEach((valDate) => {
        var row = [valDate]
        sInvSerials.forEach((serial) => {
          sFieldNames.forEach((fn) => {
            if (!headingRow) {
              headingItems.push(`${serial} ${fn}`)
            }
            row.push(data[valDate][serial][fn])
          })
        })
        if (!headingRow) {
          headingRow = headingItems.join(',')
          csvRows.push(headingRow)
        }
        csvRows.push(row.join(', '))
      })
      csvRows = csvRows.join('\n')
      // console.log(csvRows)

      var uri
      if (navigator.userAgent.indexOf('Chrome' !== -1) || navigator.userAgent.indexOf('Safari') === -1) {
        uri = 'data:text/csv,' + encodeURIComponent(csvRows)
      } else {
        uri = 'data:application/ms-excel;filename=filename.csv,' + encodeURIComponent(csvRows)
      }
      var downloadLink = document.createElement('a')
      document.body.appendChild(downloadLink)
      var ts = new Date().getTime()
      downloadLink.download = 'Report ' + ts + '.csv'
      downloadLink.href = uri
      downloadLink.click()
      document.body.removeChild(downloadLink)
    },

    downloadKwh() {
      console.log('DC about to download Kwh')

      // dataForCsv looks like this:
      //  [0] - an array of objects, one for each date in the chart
      //  [0][n].date - the date for this kWh value
      //  [0][n].kwh  - the value for this date

      var csvRows = []
      csvRows.push('Date (UTC),kWh')

      this.dataForCsv[0].forEach((item) => {
        csvRows.push(`${item.date},${item.kwh}`)
      })

      csvRows = csvRows.join('\n')

      var uri
      if (navigator.userAgent.indexOf('Chrome' !== -1) || navigator.userAgent.indexOf('Safari') === -1) {
        uri = 'data:text/csv,' + encodeURIComponent(csvRows)
      } else {
        uri = 'data:application/ms-excel;filename=filename.csv,' + encodeURIComponent(csvRows)
      }
      var downloadLink = document.createElement('a')
      document.body.appendChild(downloadLink)
      var ts = new Date().getTime()
      downloadLink.download = 'Report ' + ts + '.csv'
      downloadLink.href = uri
      downloadLink.click()
      document.body.removeChild(downloadLink)
    },

    reloadChart(startDate, endDate) {
      if (!this.readyToLoadData) {
        console.log('DC RELOADCHART NOT readyToLoadData')
        return
      }
      console.log('DC RELOADCHART')
      // data received is stored here for csv generation
      this.dataForCsv = []

      // get the lists of fields to get from the server

      this.selFieldsLeftGetFields = this.mapField(this.selFieldsLeftModel.id)
      if (this.selFieldsRightModel.id !== 'none') {
        this.selFieldsRightGetFields = this.mapField(this.selFieldsRightModel.id)
      } else {
        this.selFieldsRightGetFields = []
      }

      // console.log('DC reloadChart invIds=', this.selectedInvIds)
      // console.trace()
      // console.log('DC reloadChart left model.id', this.selFieldsLeftModel.id)
      // console.log('DC reloadChart left get=', this.selFieldsLeftGetFields)
      // console.log('DC reloadChart right model.id', this.selFieldsRightModel.id)
      // console.log('DC reloadChart right get=', this.selFieldsRightGetFields)

      // determine which fields from when endpoint to get
      let mainFields = []
      let healthFields = []
      let acFields = []
      let getKwh = false
      let getInvComp = false

      switch (this.selFieldsLeftGetFields[0]) {
        case 'main':
          mainFields = mainFields.concat(this.selFieldsLeftGetFields[1])
          break
        case 'health':
          healthFields = healthFields.concat(this.selFieldsLeftGetFields[1])
          break
        case 'ac':
          acFields = acFields.concat(this.selFieldsLeftGetFields[1])
          break
        case 'kwh':
          getKwh = true
          break
        case 'invcomp':
          getInvComp = true
          break
        default:
          console.log('DC ERROR in reload, unknown L fields type', this.selFieldsLeftGetFields)
      }

      if (this.selFieldsRightGetFields.length > 0) {
        switch (this.selFieldsRightGetFields[0]) {
          case 'main':
            mainFields = mainFields.concat(this.selFieldsRightGetFields[1])
            break
          case 'health':
            healthFields = healthFields.concat(this.selFieldsRightGetFields[1])
            break
          case 'ac':
            acFields = acFields.concat(this.selFieldsRightGetFields[1])
            break
          case 'kwh':
            getKwh = true
            break
          default:
            console.log('DC ERROR in reload, unknown R fields type', this.selFieldsRightGetFields)
        }
      }

      let inverters = this.selectedInvIds
      console.log(`selectedInvIds=${this.selectedInvIds}`)

      let invsParm = null
      if (inverters.includes(1)) {
        invsParm = '' // all inverters
      } else if (inverters.includes(0) || this.isSiteSummary) {
        invsParm = '0' // gateway summary
      } else {
        invsParm = inverters.join(',')
      }

      var siteQueryParm = ''
      if (this.isSiteSummary) {
        siteQueryParm = `site_id=${this.location.siteId}&`
      }

      console.log('DC main', mainFields)
      console.log('DC health', healthFields)
      console.log('DC ac', acFields)
      console.log('DC kwh', getKwh)

      this.pdata = []

      let mins = diffMins(startDate, endDate)
      let res = this.calculateChartRes(this.chartWidth, mins)
      // console.log(`ZIB RES=${res}  width=${this.chartWidth} mins=${mins} startDate=${startDate} endDate=${endDate} inv_serial=${invsParm}`)
      let url = `/inverters/minmaxdata?${siteQueryParm}gw_serial=${this.gwSerial}&inv_serials=${invsParm}&start_date=${startDate}&end_date=${endDate}` +
                `&group_level=inv&res=${res}&tz=${this.location.siteTz}&fields=`

      // console.trace()

      // let weather = ',cloud_cover'// request the weather (cloud cover) only in the first request we find to make

      if (mainFields.length > 0) {
        this.loadingCount++
        hiqapi.getcb(url + mainFields.join(','), this.setChartDataMinmax)
      }
      if (healthFields.length > 0) {
        this.loadingCount++
        hiqapi.getcb(url + healthFields.join(','), this.setChartDataMinmax)
      }
      if (acFields.length > 0) {
        this.loadingCount++
        hiqapi.getcb(url + acFields.join(','), this.setChartDataMinmax)
      }

      if (getKwh) {
        let group = 'both' // period and inv.
        if (invsParm === '0') {
          group = 'period' // all invs together
        }
        res = this.calculateKwhChartRes(mins)
        this.loadingCount++
        hiqapi.getcb(`/inverters/data?gw_serial=${this.gwSerial}&fields=kwh&group=${group}&start_date=${startDate}&end_date=${endDate}&res=${res}`, this.setChartDataKwh)
      }
      if (getInvComp) {
        res = '1d'
        this.loadingCount++
        hiqapi.getcb(`/inverters/data?gw_serial=${this.gwSerial}&fields=kwh&group=both&start_date=${startDate}&end_date=${endDate}&res=${res}`, this.setChartDataInvertComparison)
      }
    },
    getPercent(val) {
      return (val / this.progressMax) * 100
    },
    calculateChartRes(width, mins) {
      // Calculate the resolution we need given the width of the chart area and the number of minutes being charted
      if (mins < (width / 1440) * 1440) {
        // (1440 = mins per day)
        return '1m'
      }
      if (mins < (width / 144) * 1440) {
        // data points per day is 140, width/140 is days that will fit on graph, width/140*1440 is mins that will fit on graph
        return '10m'
      }
      if (mins < (width / 24) * 1440) {
        return '1h'
      }
      if (mins < width * 1440) {
        return '1d'
      }
      return '1mon'
    },
    calculateKwhChartRes(mins) {
      return this.resModel.id
      /*
      if (mins <= 1440) {
        return '1h'
      }
      if (mins <= 1440 * 24 * 30) { // one month
        return '1d'
      }
      if (mins <= 1440 * 24 * 30 * 10) { // 10 months
        return '1d' // 1w is supported yet    return '1w'
      }
      return '1m'
      */
    },

    setChartDataMinmax(data) {
      var items = data.items

      console.log('SET CHART DATA MINMAX', items)
      this.dataForCsv.push(items)

      let colors = [
        'rgb(90, 155, 212)', // blue
        'rgb(122, 195, 106)', // green
        'rgb(158, 103, 171)', // purple
        'rgb(241, 90, 96)', // reload
        'rgb(250, 167, 91)', // orange
        'rgb(206, 112, 88)', // brown
        'rgb(215, 127, 180)', // pinkish
      ]
      // let yaxisLabels = { plc: 'Value',
      //   power: 'Watts',
      // }
      let colorIx = 0
      items.forEach((item) => {
        let axis = null
        if (this.selFieldsLeftGetFields[1].includes(item.field)) {
          axis = 'y2'
          this.playout.yaxis2.title = this.getFieldLabel(this.selFieldsLeftModel.id)
        } else if (this.selFieldsRightGetFields[1] && this.selFieldsRightGetFields[1].includes(item.field)) {
          axis = 'y3'
          this.playout.yaxis3.title = this.getFieldLabel(this.selFieldsRightModel.id)
        }

        var invSerialText = null
        if (this.isSiteSummary || this.isGwSummary) {
          invSerialText = this.prettyFieldName(item.field)
        } else {
          invSerialText = '' + item.inv_serial + ' ' + this.prettyFieldName(item.field)
        }

        this.pdata.push({
          yaxis: axis,
          x: item.dates,
          y: item.values_min,
          type: 'scatter',
          name: invSerialText,
          mode: 'lines',
          line: { shape: 'spline', smoothing: 0.0, color: 'transparent' },
          showlegend: false,
          hoverinfo: 'skip',
        })

        this.pdata.push({
          yaxis: axis,
          x: item.dates,
          y: item.values_max,
          type: 'scatter',
          name: invSerialText,
          mode: 'lines',
          line: { shape: 'spline', smoothing: 0.0, color: colors[colorIx], width: 1 },
          showlegend: true,
          fillcolor: colors[colorIx++],
          fill: 'tonexty', // actually should be called "toprevy" in my mind
          hoverinfo: 'skip',
        })
      })
      this.playout.xaxis.type = 'datep'
      this.playout.xaxis.autorange = true // cause un-zoom
      this.playout.yaxis2.autorange = true

      this.loadingCount--
    },

    setChartDataKwh(data) {
      // The API call that returns this kwh data does not accept a list of inverters, so if only some inverters
      // were selected by the user, filter out only those.
      console.log('DC GOT KWH DATA ', data)
      this.dataForCsv.push(data.items)
      let selectedInvIds = this.selectedInvIds
      let x = {}
      let y = {}
      data.items.forEach(function(item) {
        let invSerial = 'Gateway'
        if (item.inv_serial) {
          invSerial = item.inv_serial
          if (selectedInvIds[0] !== 1 && !selectedInvIds.includes(invSerial)) {
            // selectedInvIds[0]===1 means all
            return
          }
        }
        if (!x.hasOwnProperty(invSerial)) {
          x[invSerial] = []
          y[invSerial] = []
        }

        x[invSerial].push(item.date)
        y[invSerial].push(item.kwh)
      })
      for (const inv of Object.keys(x)) {
        this.pdata.push({ x: x[inv], y: y[inv], yaxis: 'y2', type: 'bar', name: 'Inv ' + inv + 'kWh' })
      }
      this.playout.yaxis2.title = this.getFieldLabel('kwh')
      this.playout.xaxis.type = 'date'
      this.playout.xaxis.autorange = true // cause un-zoom

      this.loadingCount--
    },

    setChartDataInvertComparison(data) {
      console.log('DC GOT KWH DATA FOR I C', data)

      // get sums and cnts

      var kwhSum = {} // kwhSum[inv_serial]
      var kwhCnt = {}

      data.items.forEach((item) => {
        if (!(item.inv_serial in kwhSum)) {
          kwhSum[item.inv_serial] = 0
          kwhCnt[item.inv_serial] = 0
        }
        kwhSum[item.inv_serial] += item.kwh
        kwhCnt[item.inv_serial] += 1
      })

      // calc avgs

      let vals = [] // each entry is [serial, kwh]
      Object.keys(kwhSum).forEach((serial) => {
        var avg
        if (kwhCnt[serial] === 0) {
          avg = 0
        } else {
          avg = kwhSum[serial] / kwhCnt[serial]
        }
        vals.push([serial, avg])
      })

      // sort them by avg kwh

      var svals = vals.sort((a, b) => b[1] - a[1]) // sort by kwh

      // plot them

      var x = []
      var y = []
      svals.forEach((val) => {
        x.push(val[0])
        y.push(val[1])
      })

      this.pdata.push({ x: x, y: y, yaxis: 'y2', type: 'bar', name: 'Inv kWh' })
      this.playout.xaxis.type = 'category'
      this.playout.xaxis.autorange = true // cause un-zoom
      this.loadingCount--
    },

    setInverterList(newval) {
      console.log('DC setInveterList', newval)
      this.loadingCount = 0
      if (newval) {
        this.inverterList = newval.items
      } else {
        this.inverterList = null
      }

      // allow va-select to process the new data before retrieving the chart data
      this.$nextTick(() => this.reload())
      console.log('DC setInverterList updated inverterList')
    },

    prettyFieldName(fn) {
      if (!this.prettyFieldNames) {
        this.prettyFieldNames = new Map([
          ['output_watts', 'Watts'],
          ['kwh', 'kWh'],
          ['string1_w', 'S1 Watts'],
          ['string2_w', 'S2 Watts'],
          ['string1_v', 'S1 Volts'],
          ['string2_v', 'S2 Volts'],
          ['temp1', 'temp1'],
          ['temp2', 'temp2'],
          ['temp3', 'temp3'],
          ['condensation', 'Condensation'],
          ['plc_signal', 'PLC Signal'],
          ['plc_noise', 'PLC Noise'],
          ['phase1_vrms_min', 'P1 VRMS Min'],
          ['phase1_vrms_max', 'P1 VRMS Max'],
          ['phase2_vrms_min', 'P2 VRMS Min'],
          ['phase2_vrms_max', 'P2 VRMS Max'],
          ['phase3_vrms_min', 'P3 VRMS Min'],
          ['phase3_vrms_max', 'P3 VRMS Max'],
          ['phase1_vpeak_min', 'P1 VPEAK Min'],
          ['phase1_vpeak_max', 'P1 VPEAK Min'],
          ['phase2_vpeak_min', 'P1 VPEAK Min'],
          ['phase2_vpeak_max', 'P1 VPEAK Min'],
          ['phase3_vpeak_min', 'P1 VPEAK Min'],
          ['phase3_vpeak_max', 'P1 VPEAK Min'],
          ['phase1_irms_min', 'P1 VPEAK Min'],
          ['phase1_irms_max', 'P1 VPEAK Min'],
          ['phase2_irms_min', 'P1 VPEAK Min'],
          ['phase2_irms_max', 'P1 VPEAK Min'],
          ['phase3_irms_min', 'P1 VPEAK Min'],
          ['phase3_irms_max', 'P1 VPEAK Min'],
          ['phase1_ipeak_min', 'P1 VPEAK Min'],
          ['phase1_ipeak_max', 'P1 VPEAK Min'],
          ['phase2_ipeak_min', 'P1 VPEAK Min'],
          ['phase2_ipeak_max', 'P1 VPEAK Min'],
          ['phase3_ipeak_min', 'P1 VPEAK Min'],
          ['phase3_ipeak_max', 'P1 VPEAK Min'],
          ['freq_min', 'Freq. Min'],
          ['freq_max', 'Freq. Max'],
          ['anti_island_min', 'Anti-island Min'],
          ['anti_island_max', 'Anti-island Max'],
        ])
      }
      if (this.prettyFieldNames.has(fn)) {
        return this.prettyFieldNames.get(fn)
      }
      console.log('ERROR: Unknown field name: ', fn)
      return fn
    },
  },
}
</script>

<style scoped>
.chart {
  height: 400px;
}
.chart-spinner {
  float: left;
  z-index: 55;
  position: relative;
  margin: auto;
  width: 100%;
  padding-top: 100px;
}
.chart-help-text {
  padding-bottom: 10px;
}
.chart-help-item {
  font-style: italic;
  padding-right: 20px;
  padding-bottom: 10px;
  white-space: nowrap;
}
</style>
